import axios, { Canceler } from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { defaultCashback, defaultCashbackFilter, defaultCashbackValidation, defaultDate } from '../constants/cashback';
import { cashbackDateFormat, cashbackTimeFormat, getCashbackDateParameter } from '../helpers';
import { BRANCHES_SELECTION, ICashbackFilter, ICashbackValidationErrors, IEditCashback, TCashback } from '../types/cashback';
import { BUTTON_TITLE, LOCALES, PER_PAGE, VALIDITY_PERIOD, defaultPagination } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import { IPagination, TBooleanInNumber } from 'src/types/main';
import { arrayMerge } from 'src/utils/formatter';
import { getLocaleKeyFromError, getTranslate } from 'src/utils/helpers';
import { SCREENS } from '~/constants/screens';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';
import { messagesStore } from '~/view/containers/Messages';
import { getInputFieldErrorText } from '~/helpers/getInputFieldErrorText/getInputFieldErrorText';

export class CashbackStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return false;
  }

  cancel: Canceler | null = null;
  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable pagination: IPagination<TCashback> = defaultPagination;
  @observable filters: ICashbackFilter = defaultCashbackFilter;
  @observable cashback: IEditCashback = defaultCashback;
  @observable activeCashbacksCount: number = 0;
  @observable draggingCashbackItem: TCashback | null = null;
  @observable validationErrors: ICashbackValidationErrors = defaultCashbackValidation;
  @observable isEdit: boolean = false;
  @observable loading: boolean = false;

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing || this.loading);
  }

  @action fetchCashbackData = async (page: number = this.pagination.current_page + 1, initLoading: boolean = true) => {
    try {
      if (this.cancel) this.cancel('Cancelled');

      if (initLoading) {
        this.pagination.loading = true;
        this.pagination.refreshing = true;
      }

      const pagination = await client.get('/v3/cashback', {
        params: {
          page,
          search: this.filters.search,
          per_page: PER_PAGE.LARGE,
          status: this.filters.statusOption?.value,
        },
        headers: { 'Accept-Language': 'en,ar' },

        cancelToken: new axios.CancelToken((c) => (this.cancel = c)),
      });
      this.pagination = {
        ...pagination,
        active_total: pagination.active_total,
        pages: page > 1 ? [...(this.pagination.pages || []), pagination.current_page] : [pagination.current_page],
        data: page > 1 ? [...this.pagination.data, ...pagination.data] : pagination.data,
        loading: false,
        refreshing: false,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.loading = false;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'CashbackStore fetchCashbackData' });
    }
  };

  @action getCashback = async (id: TCashback['id']) => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      this.loading = true;

      const cashback: TCashback = await client.get(`/v3/cashback/${id}`, { headers: { 'Accept-Language': 'en,ar' } });
      this.cashback = this.setCashback(cashback);

      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'CashbackStore getCashback' });
    }
  };

  private setCashback = (cashback: TCashback): IEditCashback => {
    const allDay: boolean = cashback.start_time?.toString() === '00:00:00' && cashback.end_time?.toString() === '23:59:59';

    return {
      id: cashback.id,
      created_at: cashback.created_at,
      delayed_credit: cashback.delayed_credit,
      delayed_credit_period: String(cashback.delayed_credit_period || '0'),
      deleted_at: cashback.deleted_at,
      description: cashback.description,
      max_cashback: String(cashback.max_cashback || '0'),
      name: cashback.name,
      percent: String(cashback.percent || ''),
      branches: cashback.branches,
      branchesSelection: cashback.branches.length ? BRANCHES_SELECTION.INCLUDED : BRANCHES_SELECTION.ALL,
      order_types: cashback.order_types,
      start_time: cashbackTimeFormat(cashback.start_time),
      end_time: cashbackTimeFormat(cashback.end_time),
      all_day: allDay,
      // date
      start_day: cashbackDateFormat(cashback.start_day),
      end_day: cashbackDateFormat(cashback.end_day),
      days_of_week: cashback.days_of_week,
      //
      updated_at: cashback.updated_at,
      validity: cashback.validity,
      validity_period: String(cashback.validity_period || '0'),
      state: cashback.state,
      highest_priority: 0,
      isActiveLimitReached: this.hasActiveCashbackLimit && !cashback.state,
      isExpired: moment(`${cashback?.end_day}T${cashback?.end_time}`).isBefore(moment()),
    };
  };

  @action setDraggingCashbackItem = (item: TCashback | null) => {
    this.draggingCashbackItem = item;
  };

  @action changeCashback = (cashback: Partial<IEditCashback>) => {
    this.cashback = {
      ...this.cashback,
      ...cashback,
    };
    this.isEdit = true;
  };

  @action editCashback = async (id: TCashback['id'], crossing: boolean = false, cb?: () => void) => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      this.loading = true;

      await client.put(`/v3/cashback/${id}`, { ...this.getParams(), disable_crossing_cashback: crossing });

      this.loading = false;
      this.isEdit = false;
      this.fetchCashbackData(1);

      toastSuccess({ message: localeStore.t('toast_cashback_offer_changes_saved') });

      return true;
    } catch (error: any) {
      this._handleCashbackActionError(error, cb);
    }
  };

  private getParams = () => {
    const startTime = this.cashback.all_day ? defaultDate.start : this.cashback.start_time;
    const endTime = this.cashback.all_day ? defaultDate.end : this.cashback.end_time;

    return {
      delayed_credit: this.cashback.delayed_credit,
      delayed_credit_period: Number(this.cashback.delayed_credit_period),
      description: this.cashback.description,
      max_cashback: Number(this.cashback.max_cashback),
      name: this.cashback.name,
      percent: Number(this.cashback.percent),
      branches: this.cashback.branches.map((branch) => branch.id),
      order_types: this.cashback.order_types,
      // Attention!!! Time zones can break.
      // time
      // start_time: getCashbackTimeParameter(startTime),
      // end_time: getCashbackTimeParameter(endTime),
      // date
      start_day: getCashbackDateParameter(this.cashback.start_day, startTime),
      end_day: getCashbackDateParameter(this.cashback.end_day, endTime),
      days_of_week: this.cashback.days_of_week,
      //
      validity: this.cashback.validity,
      validity_period: Number(this.cashback.validity_period),
      status: this.cashback.state,
      set_highest_priority: this.cashback.highest_priority,
    };
  };

  @action deleteCashback = async (id: number) => {
    try {
      this.loading = true;

      await client.delete(`/v3/cashback/${id}`);

      this.loading = false;
      this.isEdit = false;

      this.fetchCashbackData(1);

      toastSuccess({ message: localeStore.t('toast_cashback_offer_deleted') });

      return true;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'CashbackStore deleteCashback', showModal: true });
    }
  };

  @action createCashback = async (crossing: boolean = false, cb?: () => void) => {
    try {
      this.loading = true;

      await client.post('/v3/cashback', { ...this.getParams(), disable_crossing_cashback: crossing });

      this.loading = false;
      this.isEdit = false;
      this.fetchCashbackData(1);
      if (cb) cb();

      toastSuccess({ message: localeStore.t('toast_cashback_offer_changes_saved') });

      return true;
    } catch (error: any) {
      this._handleCashbackActionError(error, cb);
    }
  };

  @action setFilters = (filters: ICashbackFilter, clear: boolean) => {
    if (clear) {
      this.filters = filters;
    }
    this.filters = { ...this.filters, ...filters };
    this.pagination.current_page = 0;
    this.fetchCashbackData(1);
  };

  @action changeStatus = async (cashback: number, status: boolean | number, crossing: boolean = false, name?: string, cb?: () => void) => {
    try {
      this.pagination.refreshing = true;

      const updatedCashback = await client.put(`/v3/cashback/${cashback}/status`, {
        disable_crossing_cashback: crossing,
        status,
      });
      this.pagination.data = arrayMerge(this.pagination.data, [updatedCashback], 'id');

      await this.getActiveCashbacksCount();

      this.pagination.refreshing = false;

      toastSuccess({ message: localeStore.t('toast_cashback_offer_changes_saved') });
    } catch (error: any) {
      this._handleCashbackActionError(error, cb);
    }
  };

  @action updateCashbackSorting = async (id: number, newPriority: number) => {
    try {
      this.pagination.refreshing = true;

      await client.put(`/v3/cashback/${id}/priority`, { priority: newPriority });
      await this.fetchCashbackData(1, false);

      this.setDraggingCashbackItem(null);

      toastSuccess({ message: localeStore.t('toast_cashback_priority_changed') });
    } catch (error: any) {
      this.pagination.refreshing = false;
      logger.log(error);
    }
  };

  @action getActiveCashbacksCount = async () => {
    try {
      const { count } = await client.get('v3/cashback/active/count');
      this.activeCashbacksCount = count;
    } catch (error: any) {
      logger.log(error);
    }
  };

  @action handleCashbackUpdateBySocket = async (id: number, state: TBooleanInNumber) => {
    this.pagination.data = this.pagination.data.map((cashback) => {
      if (cashback.id !== id) return cashback;
      return { ...cashback, state };
    });

    await this.getActiveCashbacksCount();

    if (window.location.pathname.includes(SCREENS.CASHBACK_CREATE())) {
      this.updateCashbackStateBeforeCreation();
    }

    if (window.location.pathname.includes(SCREENS.CASHBACK_CREATE())) {
      this.updateCashbackStateBeforeCreation();
    }
  };

  @action updateCashbackStateBeforeCreation = () => {
    this.cashback.state = this.hasActiveCashbackLimit ? 0 : 1;
    if (!this.cashback.state) this.cashback.highest_priority = 0;
    this.cashback.isActiveLimitReached = this.hasActiveCashbackLimit && !this.cashback.state;
  };

  private _handleCashbackActionError = (error: any, cb?: () => void) => {
    if (error.message === 'Cancelled') return;

    this.loading = false;
    this.pagination.refreshing = false;

    const { name: activeOffer } = error.response.data;
    const locale = getLocaleKeyFromError(error);

    if (locale === 'expired_cashback_tooltip' && activeOffer) {
      messagesStore.addMessage({
        message: localeStore.t(locale, { offerName: `"${getTranslate(activeOffer)}"` }),
        onRequestClose: () => cb?.(),
        options: [{ title: localeStore.t(BUTTON_TITLE.OK), appearance: 'tertiary', stretched: true }],
        contentClassName: 'message__content_small',
      });
    } else {
      logger.error({ error, place: 'CashbackStore _handleCashbackActionError', showModal: true });
    }
  };

  validate = () => {
    this.validateName();
    this.validateDescription();
    this.validateDate();
    this.validatePercentage();
    this.validateOrderTypes();
    this.validateBalanceValidityPeriod();
  };

  @action validateName = () => {
    this.validationErrors.nameEn = getInputFieldErrorText(this.cashback.name?.en, LOCALES.EN);
    this.validationErrors.nameAr = getInputFieldErrorText(this.cashback.name?.ar, LOCALES.AR);
  };

  @action validateDescription = () => {
    this.validationErrors.descriptionEn = getInputFieldErrorText(this.cashback.description?.en, LOCALES.EN, 100);
    this.validationErrors.descriptionAr = getInputFieldErrorText(this.cashback.description?.ar, LOCALES.AR, 100);
  };

  @action validateDate = () => {
    this.validationErrors.date = !this.cashback.start_day || !this.cashback.end_day ? localeStore.t('required_field_error') : null;
  };

  @action validatePercentage = () => {
    this.validationErrors.percent =
      Number(this.cashback.percent) === 0 || Number(this.cashback.percent) > 100 ? localeStore.t('cashback_count_error') : null;
  };

  @action validateBalanceValidityPeriod = () => {
    this.validationErrors.balanceValidityPeriod =
      this.cashback.validity !== VALIDITY_PERIOD.FOREVER && this.cashback.validity_period === '0'
        ? localeStore.t('cashback_balance_validity_hint')
        : null;
  };

  @action validateOrderTypes = () => {
    this.validationErrors.orderTypes = !this.cashback.order_types.length ? localeStore.t('atleast_one_order_type') : null;
  };

  @computed get isValidationFailed() {
    return Object.values(this.validationErrors).some((error) => error !== null);
  }

  @computed get hasActiveCashbackLimit() {
    return this.activeCashbacksCount >= 10;
  }

  @action reset = () => {
    this.cashback = defaultCashback;
    this.isEdit = false;
    this.validationErrors = defaultCashbackValidation;
  };
}

export const cashbackStore = new CashbackStore();
